import React, { useState } from 'react';
import axios from 'axios';
import { endpoints } from '../api/api';

interface FeedbackModalProps {
  type: 'post_submission' | 'general';
  bookId?: string;
  onClose: () => void;
}

const LABELS = ['Terrible', 'Poor', 'Okay', 'Good', 'Excellent'];

const FeedbackModal: React.FC<FeedbackModalProps> = ({ type, bookId, onClose }) => {
  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!rating) return;
    setSubmitting(true);
    setError(null);
    try {
      await axios.post(endpoints.submitFeedback, { rating, message, type, bookId });
      setDone(true);
    } catch {
      setError('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const active = hovered || rating;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        {done ? (
          <div className="p-8 text-center">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-gray-900 font-semibold text-base mb-1">Thanks for your feedback!</p>
            <p className="text-gray-500 text-sm">It helps us make IndiePress better for everyone.</p>
            <button onClick={onClose} className="mt-6 bg-gray-900 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors">
              Close
            </button>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="px-6 pt-6 pb-4">
              <div className="flex items-start justify-between mb-1">
                <p className="text-gray-900 font-semibold text-base">
                  {type === 'post_submission' ? 'How was your experience?' : 'Share your feedback'}
                </p>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors ml-2 mt-0.5">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-gray-400 text-xs">
                {type === 'post_submission'
                  ? 'Your book is submitted. Tell us how the process felt.'
                  : 'Help us improve IndiePress.'}
              </p>
            </div>

            {/* Stars */}
            <div className="px-6 pb-4">
              <div className="flex gap-2 justify-center mb-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHovered(star)}
                    onMouseLeave={() => setHovered(0)}
                    className="transition-transform hover:scale-110 active:scale-95"
                    aria-label={LABELS[star - 1]}
                  >
                    <svg
                      className={`w-9 h-9 transition-colors ${star <= active ? 'text-orange-400' : 'text-gray-200'}`}
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                  </button>
                ))}
              </div>
              {active > 0 && (
                <p className="text-center text-xs text-gray-500 h-4 transition-all">{LABELS[active - 1]}</p>
              )}
              {!active && <div className="h-4" />}
            </div>

            {/* Message */}
            <div className="px-6 pb-2">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                placeholder="Anything you'd like to share? (optional)"
                className="w-full text-sm text-gray-700 border border-gray-200 rounded-lg px-3 py-2.5 resize-none focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent placeholder-gray-300"
              />
              {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
            </div>

            {/* Actions */}
            <div className="px-6 pb-6 flex gap-2 pt-2">
              <button
                onClick={onClose}
                className="flex-1 bg-white border border-gray-200 text-gray-500 text-sm font-medium py-2.5 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Skip
              </button>
              <button
                onClick={handleSubmit}
                disabled={!rating || submitting}
                className="flex-1 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium py-2.5 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {submitting ? 'Sending...' : 'Submit'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FeedbackModal;
